// Industry-preset card content. When a new user picks their industry on
// signup, the card editor pre-loads with these placeholders. Densities ship as
// minimal (3 items), curated (5), or kitchen-sink (7-8). The user picks one;
// subscription gates which fits — free hits the 5-link cap at curated.
//
// Form configs are the persona-recommended Pro-tier templates. Free users get
// the same form template, but custom fields are stripped at apply time so the
// link respects the plan-tier cap.

import { ContactFormConfig, ContactFormFieldConfig } from "./api";

export type Density = "minimal" | "curated" | "kitchen-sink";

export type Industry =
  | "recruiters"
  | "sales"
  | "real-estate"
  | "founders"
  | "consultants"
  | "teams";

export interface PresetLink {
  /** Title shown in the link list and used as button label for contact forms. */
  title: string;
  /** Suggested URL the user fills in. May be a hint like "https://your-cal.com". */
  urlHint?: string;
  /** SocialIcon name. */
  icon?: string;
  /** Category for the public card grouping. */
  category?: string;
  /** "url" or "contact_form". */
  linkType: "url" | "contact_form";
  /** Form config for contact_form links. */
  formConfig?: ContactFormConfig;
  /** Description shown under the link in the editor + on the public card. */
  description?: string;
}

export interface IndustryPreset {
  industry: Industry;
  /** Display label for the picker tile. */
  label: string;
  /** Tile blurb (1 line). */
  blurb: string;
  /** Stat shown on the picker tile (e.g. "8 links"). */
  tileStat: string;
  /** Tile sub-stat (e.g. "Pre-loaded for career-fair work."). */
  tileStatCaption: string;
  /** Inline preview list shown under the tile stat. */
  tilePreview: string;
  /** "persona" or "team" — teams routes to /teams instead of card creation. */
  kind: "persona" | "team";
  /** When kind=team, where the picker routes them. */
  redirectTo?: string;
  /** Suggested card position/title placeholder. */
  positionPlaceholder?: string;
  /** Density buckets. Each is a flat list of preset links. */
  densities?: {
    minimal: PresetLink[];
    curated: PresetLink[];
    "kitchen-sink": PresetLink[];
  };
}

// ─── Helpers for building form configs ──────────────────────────────────────
const fid = (slug: string) => `f_${slug}`;
const sel = (id: string, label: string, options: string[], required = true): ContactFormFieldConfig => ({
  id, label, type: "select", required, options,
});
const txt = (id: string, label: string, required = true): ContactFormFieldConfig => ({
  id, label, type: "short_text", required,
});
const yesno = (id: string, label: string, required = true): ContactFormFieldConfig => ({
  id, label, type: "yes_no", required,
});

// ─── Recruiters ─────────────────────────────────────────────────────────────
const recruitersForm: ContactFormConfig = {
  buttonLabel: "Apply for this role",
  description: "Drop your contact and a few quick details.",
  customFields: [
    sel(fid("role"), "Role you're interested in", ["Engineering", "Product", "Design", "Sales", "Operations", "Other"]),
    sel(fid("years"), "Years of experience", ["0-2", "3-5", "6-10", "10+"]),
    txt(fid("start"), "Earliest start date", false),
  ],
};

// ─── Sales ──────────────────────────────────────────────────────────────────
const salesForm: ContactFormConfig = {
  buttonLabel: "Talk to sales",
  description: "Tell us about your team and we'll send the right deck.",
  customFields: [
    sel(fid("size"), "Company size", ["1-10", "11-50", "51-200", "201-1000", "1000+"]),
    sel(fid("budget"), "Budget range", ["< $5k", "$5-25k", "$25-100k", "$100k+"]),
    sel(fid("timeline"), "Timeline", ["This month", "This quarter", "This year", "Just exploring"]),
    txt(fid("usecase"), "Use case", false),
  ],
};

// ─── Real estate ────────────────────────────────────────────────────────────
const realEstateForm: ContactFormConfig = {
  buttonLabel: "Sign in to the open house",
  description: "Quick contact and a few details — we'll follow up tonight.",
  customFields: [
    yesno(fid("agent"), "Are you currently working with an agent?"),
    sel(fid("timeline"), "Buying timeline", ["This month", "Next 3 months", "Next 6 months", "Just looking"]),
    yesno(fid("preapproved"), "Pre-approved for a mortgage?"),
  ],
};

// ─── Founders ───────────────────────────────────────────────────────────────
const foundersForm: ContactFormConfig = {
  buttonLabel: "Pitch me",
  description: "A few quick details so I can route this right.",
  customFields: [
    sel(fid("kind"), "What's this about?", ["Investor intro", "Partnership", "Customer / pilot", "Hiring", "Press / podcast", "Other"]),
    sel(fid("role"), "Are you investing or building?", ["Investing", "Building", "Both", "Neither"]),
    txt(fid("context"), "One-line context"),
  ],
};

// ─── Consultants ────────────────────────────────────────────────────────────
const consultantsForm: ContactFormConfig = {
  buttonLabel: "Pre-engagement intake",
  description: "A few questions before we get on a call.",
  customFields: [
    txt(fid("company"), "Company / role"),
    sel(fid("kind"), "Engagement type", ["Strategy review", "Implementation", "Advisory / fractional", "Other"]),
    sel(fid("size"), "Engagement size", ["< $25k", "$25-100k", "$100-500k", "$500k+"]),
    sel(fid("when"), "When do you need to start?", ["This month", "This quarter", "Next quarter", "Exploring"]),
  ],
};

// ─── Persona presets ────────────────────────────────────────────────────────

export const INDUSTRY_PRESETS: IndustryPreset[] = [
  {
    industry: "recruiters",
    label: "Recruiters",
    blurb: "Career fairs, candidate intake, the booth QR you can scan from across the room.",
    tileStat: "8 links",
    tileStatCaption: "Pre-loaded for career-fair work.",
    tilePreview: "Open roles · ATS · Calendar · Apply form · LinkedIn · Careers page · Resume · Booth QR.",
    kind: "persona",
    positionPlaceholder: "Senior Technical Recruiter",
    densities: {
      minimal: [
        { title: "Calendar",                icon: "calendar", category: "Calendar", urlHint: "https://calendly.com/your-handle", linkType: "url" },
        { title: "Open roles",              icon: "globe",    category: "Business", urlHint: "https://yourcompany.com/careers", linkType: "url" },
        { title: "Apply for this role",     icon: "mail",     category: "Contact",  linkType: "contact_form", formConfig: recruitersForm },
      ],
      curated: [
        { title: "Calendar",                icon: "calendar", category: "Calendar", urlHint: "https://calendly.com/your-handle", linkType: "url" },
        { title: "Open roles",              icon: "globe",    category: "Business", urlHint: "https://yourcompany.com/careers", linkType: "url" },
        { title: "LinkedIn",                icon: "linkedin", category: "Social Media", urlHint: "https://linkedin.com/in/your-handle", linkType: "url" },
        { title: "Careers page",            icon: "globe",    category: "Business", urlHint: "https://yourcompany.com/careers", linkType: "url" },
        { title: "Apply for this role",     icon: "mail",     category: "Contact",  linkType: "contact_form", formConfig: recruitersForm },
      ],
      "kitchen-sink": [
        { title: "Calendar",                icon: "calendar", category: "Calendar", urlHint: "https://calendly.com/your-handle", linkType: "url" },
        { title: "Open roles",              icon: "globe",    category: "Business", urlHint: "https://yourcompany.com/careers", linkType: "url" },
        { title: "LinkedIn",                icon: "linkedin", category: "Social Media", urlHint: "https://linkedin.com/in/your-handle", linkType: "url" },
        { title: "Careers page",            icon: "globe",    category: "Business", urlHint: "https://yourcompany.com/careers", linkType: "url" },
        { title: "Greenhouse",              icon: "link",     category: "Business", urlHint: "https://boards.greenhouse.io/your-company", linkType: "url" },
        { title: "Lever",                   icon: "link",     category: "Business", urlHint: "https://jobs.lever.co/your-company", linkType: "url" },
        { title: "Resume PDF",              icon: "link",     category: "Content",  urlHint: "https://your-resume.pdf", linkType: "url" },
        { title: "Apply for this role",     icon: "mail",     category: "Contact",  linkType: "contact_form", formConfig: recruitersForm },
      ],
    },
  },
  {
    industry: "sales",
    label: "Sales",
    blurb: "Demos, decks, lead capture for AEs and BDRs at the trade-show floor.",
    tileStat: "7 links",
    tileStatCaption: "Pre-loaded for AEs and BDRs.",
    tilePreview: "Demo calendar · Deck · Case studies · Pricing · LinkedIn · Loom · Talk-to-sales form.",
    kind: "persona",
    positionPlaceholder: "Account Executive",
    densities: {
      minimal: [
        { title: "Book a demo",             icon: "calendar", category: "Calendar", urlHint: "https://calendly.com/your-handle/demo", linkType: "url" },
        { title: "Sales deck (PDF)",        icon: "link",     category: "Content",  urlHint: "https://your-deck.pdf", linkType: "url" },
        { title: "Talk to sales",           icon: "mail",     category: "Contact",  linkType: "contact_form", formConfig: salesForm },
      ],
      curated: [
        { title: "Book a demo",             icon: "calendar", category: "Calendar", urlHint: "https://calendly.com/your-handle/demo", linkType: "url" },
        { title: "Sales deck (PDF)",        icon: "link",     category: "Content",  urlHint: "https://your-deck.pdf", linkType: "url" },
        { title: "Case studies",            icon: "link",     category: "Content",  urlHint: "https://yourcompany.com/customers", linkType: "url" },
        { title: "Pricing",                 icon: "dollar",   category: "Business", urlHint: "https://yourcompany.com/pricing", linkType: "url" },
        { title: "Talk to sales",           icon: "mail",     category: "Contact",  linkType: "contact_form", formConfig: salesForm },
      ],
      "kitchen-sink": [
        { title: "Book a demo",             icon: "calendar", category: "Calendar", urlHint: "https://calendly.com/your-handle/demo", linkType: "url" },
        { title: "Sales deck (PDF)",        icon: "link",     category: "Content",  urlHint: "https://your-deck.pdf", linkType: "url" },
        { title: "Case studies",            icon: "link",     category: "Content",  urlHint: "https://yourcompany.com/customers", linkType: "url" },
        { title: "Pricing one-pager",       icon: "dollar",   category: "Business", urlHint: "https://yourcompany.com/pricing", linkType: "url" },
        { title: "LinkedIn",                icon: "linkedin", category: "Social Media", urlHint: "https://linkedin.com/in/your-handle", linkType: "url" },
        { title: "Loom intro",              icon: "video",    category: "Content",  urlHint: "https://loom.com/share/your-intro", linkType: "url" },
        { title: "Talk to sales",           icon: "mail",     category: "Contact",  linkType: "contact_form", formConfig: salesForm },
      ],
    },
  },
  {
    industry: "real-estate",
    label: "Real estate",
    blurb: "Listings, tours, the open-house QR. Replaces the clipboard sign-in.",
    tileStat: "7 links",
    tileStatCaption: "Pre-loaded for showing agents.",
    tilePreview: "Listings · Zillow · Matterport · Schedule a tour · Mortgage calc · Neighborhood guide · Open-house sign-in.",
    kind: "persona",
    positionPlaceholder: "Realtor",
    densities: {
      minimal: [
        { title: "Active listings",         icon: "home",     category: "Real Estate", urlHint: "https://yourbrokerage.com/agent/your-handle", linkType: "url" },
        { title: "Schedule a tour",         icon: "calendar", category: "Calendar",    urlHint: "https://calendly.com/your-handle/tour", linkType: "url" },
        { title: "Open-house sign-in",      icon: "mail",     category: "Contact",     linkType: "contact_form", formConfig: realEstateForm },
      ],
      curated: [
        { title: "Active listings",         icon: "home",     category: "Real Estate", urlHint: "https://yourbrokerage.com/agent/your-handle", linkType: "url" },
        { title: "Zillow profile",          icon: "globe",    category: "Real Estate", urlHint: "https://zillow.com/profile/your-handle", linkType: "url" },
        { title: "Matterport tours",        icon: "video",    category: "Real Estate", urlHint: "https://my.matterport.com/show/?m=your-tour", linkType: "url" },
        { title: "Schedule a tour",         icon: "calendar", category: "Calendar",    urlHint: "https://calendly.com/your-handle/tour", linkType: "url" },
        { title: "Open-house sign-in",      icon: "mail",     category: "Contact",     linkType: "contact_form", formConfig: realEstateForm },
      ],
      "kitchen-sink": [
        { title: "Active listings",         icon: "home",     category: "Real Estate", urlHint: "https://yourbrokerage.com/agent/your-handle", linkType: "url" },
        { title: "Zillow profile",          icon: "globe",    category: "Real Estate", urlHint: "https://zillow.com/profile/your-handle", linkType: "url" },
        { title: "Matterport tours",        icon: "video",    category: "Real Estate", urlHint: "https://my.matterport.com/show/?m=your-tour", linkType: "url" },
        { title: "Schedule a tour",         icon: "calendar", category: "Calendar",    urlHint: "https://calendly.com/your-handle/tour", linkType: "url" },
        { title: "Mortgage calculator",     icon: "dollar",   category: "Real Estate", urlHint: "https://yourbrokerage.com/mortgage", linkType: "url" },
        { title: "Neighborhood guide",      icon: "link",     category: "Content",     urlHint: "https://yourbrokerage.com/neighborhoods", linkType: "url" },
        { title: "Open-house sign-in",      icon: "mail",     category: "Contact",     linkType: "contact_form", formConfig: realEstateForm },
      ],
    },
  },
  {
    industry: "founders",
    label: "Founders",
    blurb: "Press kit, deck, demo loom. The card you actually want to hand an investor.",
    tileStat: "7 links",
    tileStatCaption: "Pre-loaded for founders raising or going to market.",
    tilePreview: "Press kit · Pitch deck · Demo loom · Calendar · LinkedIn · AngelList · Pitch-me form.",
    kind: "persona",
    positionPlaceholder: "Co-founder & CEO",
    densities: {
      minimal: [
        { title: "Pitch deck",              icon: "link",     category: "Content",   urlHint: "https://your-deck.pdf", linkType: "url" },
        { title: "Calendar",                icon: "calendar", category: "Calendar",  urlHint: "https://calendly.com/your-handle", linkType: "url" },
        { title: "Pitch me",                icon: "mail",     category: "Contact",   linkType: "contact_form", formConfig: foundersForm },
      ],
      curated: [
        { title: "Pitch deck",              icon: "link",     category: "Content",   urlHint: "https://your-deck.pdf", linkType: "url" },
        { title: "Demo loom",               icon: "video",    category: "Content",   urlHint: "https://loom.com/share/your-demo", linkType: "url" },
        { title: "Press kit",               icon: "link",     category: "Content",   urlHint: "https://yourcompany.com/press", linkType: "url" },
        { title: "Calendar",                icon: "calendar", category: "Calendar",  urlHint: "https://calendly.com/your-handle", linkType: "url" },
        { title: "Pitch me",                icon: "mail",     category: "Contact",   linkType: "contact_form", formConfig: foundersForm },
      ],
      "kitchen-sink": [
        { title: "Pitch deck",              icon: "link",     category: "Content",   urlHint: "https://your-deck.pdf", linkType: "url" },
        { title: "Demo loom",               icon: "video",    category: "Content",   urlHint: "https://loom.com/share/your-demo", linkType: "url" },
        { title: "Press kit",               icon: "link",     category: "Content",   urlHint: "https://yourcompany.com/press", linkType: "url" },
        { title: "Calendar",                icon: "calendar", category: "Calendar",  urlHint: "https://calendly.com/your-handle", linkType: "url" },
        { title: "LinkedIn",                icon: "linkedin", category: "Social Media", urlHint: "https://linkedin.com/in/your-handle", linkType: "url" },
        { title: "AngelList / OpenVC",      icon: "link",     category: "Business",  urlHint: "https://angel.co/u/your-handle", linkType: "url" },
        { title: "Pitch me",                icon: "mail",     category: "Contact",   linkType: "contact_form", formConfig: foundersForm },
      ],
    },
  },
  {
    industry: "consultants",
    label: "Consultants",
    blurb: "Engagement deck, intake, calendar. Pre-qualify before the discovery call gets booked.",
    tileStat: "7 links",
    tileStatCaption: "Pre-loaded for independents and boutiques.",
    tilePreview: "Engagement deck · Case studies · Intake form · Calendar · LinkedIn · Substack · Whitepaper.",
    kind: "persona",
    positionPlaceholder: "Independent Strategy Consultant",
    densities: {
      minimal: [
        { title: "Engagement deck",         icon: "link",     category: "Content",   urlHint: "https://your-deck.pdf", linkType: "url" },
        { title: "Calendar",                icon: "calendar", category: "Calendar",  urlHint: "https://calendly.com/your-handle", linkType: "url" },
        { title: "Pre-engagement intake",   icon: "mail",     category: "Contact",   linkType: "contact_form", formConfig: consultantsForm },
      ],
      curated: [
        { title: "Engagement deck",         icon: "link",     category: "Content",   urlHint: "https://your-deck.pdf", linkType: "url" },
        { title: "Case studies",            icon: "link",     category: "Content",   urlHint: "https://yourpractice.com/case-studies", linkType: "url" },
        { title: "Calendar",                icon: "calendar", category: "Calendar",  urlHint: "https://calendly.com/your-handle", linkType: "url" },
        { title: "LinkedIn",                icon: "linkedin", category: "Social Media", urlHint: "https://linkedin.com/in/your-handle", linkType: "url" },
        { title: "Pre-engagement intake",   icon: "mail",     category: "Contact",   linkType: "contact_form", formConfig: consultantsForm },
      ],
      "kitchen-sink": [
        { title: "Engagement deck",         icon: "link",     category: "Content",   urlHint: "https://your-deck.pdf", linkType: "url" },
        { title: "Case studies",            icon: "link",     category: "Content",   urlHint: "https://yourpractice.com/case-studies", linkType: "url" },
        { title: "Calendar",                icon: "calendar", category: "Calendar",  urlHint: "https://calendly.com/your-handle", linkType: "url" },
        { title: "LinkedIn",                icon: "linkedin", category: "Social Media", urlHint: "https://linkedin.com/in/your-handle", linkType: "url" },
        { title: "Substack",                icon: "link",     category: "Content",   urlHint: "https://yourname.substack.com", linkType: "url" },
        { title: "Whitepaper",              icon: "link",     category: "Content",   urlHint: "https://yourpractice.com/whitepaper", linkType: "url" },
        { title: "Pre-engagement intake",   icon: "mail",     category: "Contact",   linkType: "contact_form", formConfig: consultantsForm },
      ],
    },
  },
  {
    industry: "teams",
    label: "Teams",
    blurb: "Provisioning cards for everyone — admin dashboard, brand-locked themes, bulk ordering.",
    tileStat: "$3.99",
    tileStatCaption: "per person per month, no annual contract.",
    tilePreview: "Centralized branding · Admin dashboard · Bulk physical cards · Aggregated analytics.",
    kind: "team",
    redirectTo: "/teams",
  },
];

/** Lookup by industry id. */
export function getPreset(industry: Industry | string | null | undefined): IndustryPreset | null {
  if (!industry) return null;
  return INDUSTRY_PRESETS.find((p) => p.industry === industry) ?? null;
}

/** Free-tier link cap — used by density gating. */
export const FREE_LINK_CAP = 5;

/** Strip custom fields from any contact_form preset link. Used when applying
 *  a preset for a free-tier user. */
export function stripCustomFieldsForFree(links: PresetLink[]): PresetLink[] {
  return links.map((l) => {
    if (l.linkType !== "contact_form" || !l.formConfig) return l;
    return {
      ...l,
      formConfig: {
        ...l.formConfig,
        customFields: [],
      },
    };
  });
}
